import { useState } from "react";
import { Link, useLocation } from "wouter";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { isValidEmail } from "@/lib/utils";
import { apiRequest } from "@/lib/queryClient";
import { EVENTS, identify, track } from "@/lib/telemetry";
import { useNavigationContent, useFormContent, useMessageContent, usePageTitleContent } from "@/hooks/useDynamicContent";
import { AUTH_QUERY_KEY, fetchCurrentUser } from "@/hooks/useAuth";
import SettingsToggle from "@/components/SettingsToggle";
import BrandLogo from "@/components/BrandLogo";
import { ArrowLeft, Eye, EyeOff } from "lucide-react";
import { authReturnFromSearch, withAuthReturn } from "@/lib/authReturn";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [, setLocation] = useLocation();
  const returnTo = authReturnFromSearch(window.location.search);

  // Dynamic content hooks
  const { getContent: getNavigation } = useNavigationContent();
  const { getContent: getForm } = useFormContent();
  const { getContent: getMessage } = useMessageContent();
  const { getContent: getPageTitle } = usePageTitleContent();

  const loginMutation = useMutation({
    mutationFn: async (data: { email: string; password: string }) => {
      const response = await apiRequest("POST", "/api/auth/login", data);
      return response.json();
    },
    onSuccess: async () => {
      // Verify the cookie-backed session before exposing the authenticated app.
      // This is intentionally a real request after the login POST, rather than
      // relying on the POST response's user payload.
      const user = await fetchCurrentUser();
      if (!user) {
        throw new Error("Login session could not be verified. Please try again.");
      }
      queryClient.setQueryData(AUTH_QUERY_KEY, user);
      if (user.id) identify(String(user.id), { surface: "web" });

      track(EVENTS.signIn, { surface: "web", method: "password" });

      toast({
        title: getMessage("success.login", "Welcome Back!"),
        description: getMessage("success.login_description", "Successfully logged in to your cosmic profile."),
      });

      setLocation(returnTo || "/");
    },
    onError: (error) => {
      toast({
        title: getMessage("error.login_failed", "Login Failed"),
        description: error.message || getMessage("error.login_failed_description", "Please check your credentials and try again."),
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      toast({
        title: getMessage("error.required_fields", "Error"),
        description: getMessage("error.required_email_password", "Please enter both email and password"),
        variant: "destructive",
      });
      return;
    }
    if (!isValidEmail(email)) {
      toast({
        title: getMessage("error.invalid_email_title", "Invalid email"),
        description: getMessage("error.invalid_email", "Please enter a valid email address (e.g. name@example.com)."),
        variant: "destructive",
      });
      return;
    }
    loginMutation.mutate({ email, password });
  };

  return (
    <div className="min-h-screen cosmic-bg flex items-center justify-center py-4" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-between mb-3">
          <Link href="/landing">
            <Button variant="ghost" size="icon" className="h-9 w-9 rounded-full" aria-label="Back">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <SettingsToggle />
        </div>
        <div className="text-center mb-4">
          <div className="animate-float mb-3">
            <BrandLogo width={190} />
          </div>
          <p className="text-muted-foreground text-sm">
            {getMessage("message.welcome_back", "Welcome back to your cosmic journey")}
          </p>
        </div>

        <Card className="cosmic-card border-0 shadow-lg overflow-hidden" data-testid="login-form-card">
          <div className="h-1 bg-gradient-to-r from-violet-500 via-fuchsia-500 to-pink-500" />
          <CardHeader className="pb-4">
            <CardTitle className="text-center text-lg font-medium text-foreground">
              {getPageTitle("page.title.login", "Sign In")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
              <div className="space-y-1">
                <Label htmlFor="email" className="text-sm font-medium text-foreground">
                  {getForm("form.email", "Email")}
                </Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="replit-input"
                  placeholder={getForm("form.placeholder.email", "Enter your email")}
                  data-testid="input-email"
                  required
                />
              </div>

              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-sm font-medium text-foreground">
                    {getForm("form.password", "Password")}
                  </Label>
                  <Link href="/forgot-password">
                    <span className="text-xs text-primary hover:text-primary/80 cursor-pointer">
                      Forgot password?
                    </span>
                  </Link>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="replit-input pr-10 text-lg tracking-widest"
                    placeholder={getForm("form.placeholder.password", "Enter your password")}
                    data-testid="input-password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
                    data-testid="button-toggle-password"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <Button 
                type="submit" 
                disabled={loginMutation.isPending}
                className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 text-white"
                data-testid="button-login"
              >
                {loginMutation.isPending ? (
                  <span className="flex items-center">
                    <i className="fas fa-spinner fa-spin mr-2" aria-hidden="true"></i>
                    {getMessage("loading.signing_in", "Signing In...")}
                  </span>
                ) : (
                  <span className="flex items-center">
                    <i className="fas fa-sign-in-alt mr-2" aria-hidden="true"></i>
                    {getNavigation("nav.sign_in", "Sign In")}
                  </span>
                )}
              </Button>
            </form>

            <div className="text-center mt-6">
              <p className="text-sm text-muted-foreground">
                Don't have an account?{" "}
                <Link href={returnTo ? withAuthReturn("/signup", returnTo) : "/signup"}>
                  <span className="text-primary hover:text-primary/80 font-medium cursor-pointer" data-testid="link-signup">
                    Sign up here
                  </span>
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}